import { z } from 'zod';
import { IDEMPOTENCY_WINDOW_MS } from './constants';
import {
  getRateLimitDelay,
  markRateLimited,
  markTypeErrorRateLimited,
  RateLimitedError,
  resetTypeErrorBackoff,
} from './rate-limit';
import {
  type ApiListResponse,
  type BatchDetail,
  type BatchListItem,
  type BatchResultItem,
  type BatchStatusResponse,
  type BillingPeriod,
  type BillingPortalResponse,
  type CreditBalance,
  type CreditPack,
  type CreditPackCheckoutResponse,
  type DesignDetail,
  type DesignListItem,
  type Listing,
  type ListingUpdatePayload,
  type Subscription,
  type SubscriptionCheckoutResponse,
  type SubscriptionTier,
  type TaskStatusResponse,
  type TransactionHistoryResponse,
  type RefundEligibility,
  type RefundResponse,
  type UploadDesignResponse,
} from './types';

const DEFAULT_API_BASE = '/api/v1';

const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? DEFAULT_API_BASE;

let cachedToken = '';
let tokenRefresher: (() => Promise<string | null>) | null = null;

// Lock to prevent concurrent token refreshes
let refreshPromise: Promise<string | null> | null = null;

const getHeaders = (isJson = true): HeadersInit => {
  const headers: HeadersInit = {};
  if (isJson) headers['content-type'] = 'application/json';
  if (cachedToken) headers.authorization = `Bearer ${cachedToken}`;
  return headers;
};

/**
 * Generate an idempotency key for task-creating requests.
 * Key is based on entity ID and current minute to allow retries
 * within the same minute window while preventing duplicates.
 */
const generateIdempotencyKey = (prefix: string, entityId: string): string => {
  // Round to minute for retry window
  const timeWindow = Math.floor(Date.now() / IDEMPOTENCY_WINDOW_MS);
  return `${prefix}-${entityId}-${timeWindow}`;
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    if (response.status === 429) {
      // Get retry duration: header first, then body, then default 60s
      let delayMs = 60_000;
      const retryAfterHeader = response.headers.get('retry-after');
      if (retryAfterHeader) {
        delayMs = parseInt(retryAfterHeader, 10) * 1000;
      } else {
        try {
          const body: { retry_after?: number } = await response.json();
          if (body.retry_after) delayMs = body.retry_after * 1000;
        } catch {
          // Body parse failed, use default
        }
      }
      markRateLimited(delayMs);
      // Once we receive a readable 429 again, reset the generic TypeError
      // backoff so future network faults start from the shortest delay.
      resetTypeErrorBackoff();
      throw new RateLimitedError(delayMs);
    }
    const message = await response.text();
    throw new Error(message || `Request failed with ${response.status}`);
  }
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) return (await response.json()) as T;
  // @ts-expect-error fallback for non-json payloads
  return undefined;
};

/**
 * Refresh token with deduplication - multiple concurrent 401s share the same refresh
 */
const refreshTokenWithLock = async (): Promise<string | null> => {
  if (!tokenRefresher) return null;

  // If already refreshing, wait for that promise
  if (refreshPromise) {
    return refreshPromise;
  }

  // Start new refresh
  refreshPromise = tokenRefresher().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

/**
 * Concurrency limiter (semaphore).
 *
 * Prevents burst traffic from overwhelming the server or triggering
 * Cloudflare's edge-level rate limiting. Without this, page mount fires
 * ~50 concurrent requests — the edge throttles the burst and returns
 * CORS-less 429s that the client can't read.
 *
 * With a cap of 6, requests are serialized in small batches. Each batch
 * completes in ~100ms, so 50 requests spread over ~1s instead of 300ms.
 */
const MAX_CONCURRENT = 6;
let inFlightCount = 0;
const waitQueue: Array<() => void> = [];

function acquireSlot(): Promise<void> {
  if (inFlightCount < MAX_CONCURRENT) {
    inFlightCount++;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    waitQueue.push(() => {
      inFlightCount++;
      resolve();
    });
  });
}

function releaseSlot(): void {
  inFlightCount--;
  const next = waitQueue.shift();
  if (next) next();
}

/**
 * Wrapper for fetch that handles 401 errors by refreshing token and retrying once
 */
const fetchWithRetry = async (
  url: string,
  options: RequestInit = {},
  isRetry = false
): Promise<Response> => {
  // Fail fast if currently rate-limited (no network call)
  const rateLimitDelay = getRateLimitDelay();
  if (rateLimitDelay > 0) {
    throw new RateLimitedError(rateLimitDelay);
  }

  // Wait for a concurrency slot (queues excess requests)
  await acquireSlot();

  // Re-check rate limit after waiting — another request may have
  // triggered a 429 while this one was queued.
  const delayAfterWait = getRateLimitDelay();
  if (delayAfterWait > 0) {
    releaseSlot();
    throw new RateLimitedError(delayAfterWait);
  }

  let response: Response;
  try {
    response = await fetch(url, options);
  } catch (err) {
    // Legacy cross-origin edge throttling surfaced as TypeError because the
    // browser could not read the underlying 429. We still treat unexpected
    // TypeErrors conservatively to avoid immediate retry storms.
    if (err instanceof TypeError) {
      if (getRateLimitDelay() <= 0) {
        markTypeErrorRateLimited();
      }
      throw new RateLimitedError(getRateLimitDelay());
    }
    throw err;
  } finally {
    releaseSlot();
  }

  // If 401 and we have a refresher and haven't retried yet, try refreshing token
  if (response.status === 401 && tokenRefresher && !isRetry) {
    const newToken = await refreshTokenWithLock();
    if (newToken) {
      // Update cached token and retry with new auth header
      cachedToken = newToken;
      const newHeaders = { ...options.headers } as Record<string, string>;
      newHeaders.authorization = `Bearer ${newToken}`;
      return fetchWithRetry(url, { ...options, headers: newHeaders }, true);
    }
  }

  return response;
};

export const apiClient = {
  get apiBase(): string {
    return apiBase;
  },
  get token(): string | undefined {
    return cachedToken || undefined;
  },
  setToken(token?: string): void {
    cachedToken = token ?? '';
  },
  setTokenRefresher(refresher: (() => Promise<string | null>) | null): void {
    tokenRefresher = refresher;
  },
  async listDesigns(
    params: Record<string, string | number | boolean | undefined> = {}
  ): Promise<ApiListResponse<DesignListItem>> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) search.append(key, String(value));
    });

    const response = await fetchWithRetry(`${apiBase}/designs${search.size ? `?${search}` : ''}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async getDesign(designId: string): Promise<{ design: DesignDetail; listing: Listing | null }> {
    const response = await fetchWithRetry(`${apiBase}/designs/${designId}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async getListing(designId: string): Promise<Listing> {
    const response = await fetchWithRetry(`${apiBase}/designs/${designId}/listing`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async updateListing(designId: string, payload: ListingUpdatePayload): Promise<Listing> {
    const response = await fetchWithRetry(`${apiBase}/designs/${designId}/listing`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },
  async generateListingField(
    designId: string,
    field: 'title' | 'brand' | 'bullet_one' | 'bullet_two' | 'description',
    customInstructions?: string
  ) {
    const body = JSON.stringify({ field, custom_instructions: customInstructions });
    const headers = {
      ...getHeaders(),
      'Idempotency-Key': generateIdempotencyKey(`gen-field-${field}`, designId),
    };
    const response = await fetchWithRetry(`${apiBase}/designs/${designId}/listing/generate-field`, {
      method: 'POST',
      headers,
      body,
    });
    return handleResponse(response);
  },
  async generateListing(designId: string, customInstructions?: string) {
    const body = customInstructions
      ? JSON.stringify({ custom_instructions: customInstructions })
      : undefined;
    const headers = {
      ...getHeaders(),
      'Idempotency-Key': generateIdempotencyKey('gen', designId),
    };
    const response = await fetchWithRetry(`${apiBase}/designs/${designId}/generate`, {
      method: 'POST',
      headers,
      ...(body ? { body } : {}),
    });
    return handleResponse(response);
  },
  async checkDesign(designId: string) {
    const headers = {
      ...getHeaders(),
      'Idempotency-Key': generateIdempotencyKey('chk-design', designId),
    };
    const response = await fetchWithRetry(`${apiBase}/designs/${designId}/check`, {
      method: 'POST',
      headers,
    });
    return handleResponse(response);
  },
  async checkListing(designId: string) {
    const headers = {
      ...getHeaders(),
      'Idempotency-Key': generateIdempotencyKey('chk-listing', designId),
    };
    const response = await fetchWithRetry(`${apiBase}/designs/${designId}/listing/check`, {
      method: 'POST',
      headers,
    });
    return handleResponse(response);
  },
  async updateDesign(
    designId: string,
    payload: { filename?: string }
  ): Promise<{ design_id: string; filename: string }> {
    const response = await fetchWithRetry(`${apiBase}/designs/${designId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },
  async deleteDesign(designId: string): Promise<void> {
    const response = await fetchWithRetry(`${apiBase}/designs/${designId}`, {
      method: 'DELETE',
      headers: getHeaders(false),
    });
    if (!response.ok) throw new Error('Failed to delete design');
  },
  async deleteDesigns(designIds: string[]): Promise<{ deleted_count: number; errors: string[] }> {
    // Bulk delete via single API call
    const response = await fetchWithRetry(`${apiBase}/designs`, {
      method: 'DELETE',
      headers: getHeaders(),
      body: JSON.stringify({ design_ids: designIds }),
    });
    const result = await handleResponse<{ deleted_count: number; deleted_design_ids: string[] }>(
      response
    );
    return { deleted_count: result.deleted_count, errors: [] };
  },
  async uploadDesign(
    form: FormData,
    generate = false,
    signal?: AbortSignal
  ): Promise<UploadDesignResponse> {
    const response = await fetchWithRetry(`${apiBase}/designs${generate ? '?generate=true' : ''}`, {
      method: 'POST',
      headers: getHeaders(false),
      body: form,
      ...(signal ? { signal } : {}),
    });
    return handleResponse(response);
  },
  async listBatches(
    params: Record<string, string | number | undefined> = {}
  ): Promise<ApiListResponse<BatchListItem>> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) search.append(key, String(value));
    });
    const response = await fetchWithRetry(`${apiBase}/batches${search.size ? `?${search}` : ''}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async getBatch(batchId: string): Promise<BatchDetail> {
    const response = await fetchWithRetry(`${apiBase}/batches/${batchId}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async getBatchStatus(batchId: string): Promise<BatchStatusResponse> {
    const response = await fetchWithRetry(`${apiBase}/batches/${batchId}/status`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async getBatchResults(batchId: string): Promise<BatchResultItem[]> {
    const response = await fetchWithRetry(`${apiBase}/batches/${batchId}/results`, {
      headers: getHeaders(),
    });
    const parsed = await handleResponse<ApiListResponse<BatchResultItem>>(response);
    return parsed.data;
  },
  async createBatch(payload: {
    name?: string;
    custom_instructions?: string;
    design_ids?: string[];
  }) {
    const response = await fetchWithRetry(`${apiBase}/batches`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },
  async addDesignsToBatch(batchId: string, designIds: string[]): Promise<void> {
    const response = await fetchWithRetry(`${apiBase}/batches/${batchId}/designs`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ design_ids: designIds }),
    });
    return handleResponse(response);
  },
  async removeDesignsFromBatch(batchId: string, designIds: string[]): Promise<void> {
    const response = await fetchWithRetry(`${apiBase}/batches/${batchId}/designs`, {
      method: 'DELETE',
      headers: getHeaders(),
      body: JSON.stringify({ design_ids: designIds }),
    });
    return handleResponse(response);
  },
  async updateBatch(
    batchId: string,
    payload: { name?: string; custom_instructions?: string }
  ): Promise<{ batch_id: string; name: string; status: string }> {
    const response = await fetchWithRetry(`${apiBase}/batches/${batchId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },
  async deleteBatch(
    batchId: string,
    cascade = false
  ): Promise<{ designs_orphaned: number; designs_deleted: number }> {
    const response = await fetchWithRetry(`${apiBase}/batches/${batchId}?cascade=${cascade}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async generateBatch(
    batchId: string,
    skipExisting = true
  ): Promise<{
    batch_id: string;
    designs_to_process: number;
    credits_deducted: number;
  }> {
    const response = await fetchWithRetry(`${apiBase}/batches/${batchId}/generate`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ skip_existing: skipExisting }),
    });
    return handleResponse(response);
  },
  async listTasks(
    params: Record<string, string | number | boolean | undefined> = {}
  ): Promise<ApiListResponse<TaskStatusResponse>> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) search.append(key, String(value));
    });
    const response = await fetchWithRetry(`${apiBase}/tasks${search.size ? `?${search}` : ''}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async getTask(taskId: string): Promise<TaskStatusResponse> {
    const response = await fetchWithRetry(`${apiBase}/tasks/${taskId}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async cancelTask(taskId: string): Promise<void> {
    const response = await fetchWithRetry(`${apiBase}/tasks/${taskId}/cancel`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async markTasksSeen(taskIds: string[]): Promise<void> {
    const response = await fetchWithRetry(`${apiBase}/tasks/mark-seen`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ task_ids: taskIds }),
    });
    return handleResponse(response);
  },
  async getDesignTasks(designId: string): Promise<{ data: TaskStatusResponse[] }> {
    const response = await fetchWithRetry(`${apiBase}/designs/${designId}/tasks`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async getCredits(): Promise<CreditBalance> {
    const response = await fetchWithRetry(`${apiBase}/credits`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async getTransactionHistory(params?: {
    page?: number;
    limit?: number;
    pool?: 'subscription' | 'purchased';
  }): Promise<TransactionHistoryResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.pool) searchParams.set('pool', params.pool);
    const query = searchParams.toString();
    const url = `${apiBase}/credits/history${query ? `?${query}` : ''}`;
    const response = await fetchWithRetry(url, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async getSubscription(): Promise<Subscription> {
    const response = await fetchWithRetry(`${apiBase}/subscriptions`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async createSubscriptionCheckout(
    tier: Exclude<SubscriptionTier, 'free'>,
    billingPeriod: BillingPeriod = 'monthly'
  ): Promise<SubscriptionCheckoutResponse> {
    const response = await fetchWithRetry(`${apiBase}/subscriptions/checkout`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ tier, billing_period: billingPeriod }),
    });
    return handleResponse(response);
  },
  async createBillingPortal(): Promise<BillingPortalResponse> {
    const response = await fetchWithRetry(`${apiBase}/subscriptions/portal`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async listCreditPacks(): Promise<{ packs: CreditPack[] }> {
    const response = await fetchWithRetry(`${apiBase}/credits/packs`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async createCreditPackCheckout(packId: string): Promise<CreditPackCheckoutResponse> {
    const response = await fetchWithRetry(`${apiBase}/credits/packs/${packId}/checkout`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async exportListing(designId: string): Promise<{ download_url: string; listing_count: number }> {
    const response = await fetchWithRetry(`${apiBase}/exports`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ scope: 'single', design_id: designId }),
    });
    return handleResponse(response);
  },
  async exportSelected(designIds: string[]): Promise<{
    export_id: string;
    download_url: string;
    expires_at: string;
    listing_count: number;
  }> {
    const response = await fetchWithRetry(`${apiBase}/exports`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ scope: 'selected', design_ids: designIds }),
    });
    return handleResponse(response);
  },
  async exportFiltered(filter: 'all' | 'approved' | 'warnings' | 'rejected'): Promise<{
    export_id: string;
    download_url: string;
    expires_at: string;
    listing_count: number;
  }> {
    const response = await fetchWithRetry(`${apiBase}/exports`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ scope: 'filtered', filter }),
    });
    return handleResponse(response);
  },
  async exportAll(): Promise<{
    export_id: string;
    download_url: string;
    expires_at: string;
    listing_count: number;
  }> {
    const response = await fetchWithRetry(`${apiBase}/exports`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ scope: 'all' }),
    });
    return handleResponse(response);
  },
  async submitSupportRequest(data: {
    category: string;
    message: string;
    page_url?: string;
  }): Promise<{ request_id: string; created_at: string }> {
    const response = await fetchWithRetry(`${apiBase}/support`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  async checkRefundEligibility(
    kind: 'pack' | 'subscription',
    orderId?: string
  ): Promise<RefundEligibility> {
    const params = new URLSearchParams({ kind });
    if (orderId) params.set('order_id', orderId);
    const response = await fetchWithRetry(`${apiBase}/refunds/eligibility?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
  async checkRefundEligibilityBatch(
    orderIds: string[],
    includeSubscription = false
  ): Promise<{ packs: Record<string, RefundEligibility>; subscription?: RefundEligibility }> {
    const response = await fetchWithRetry(`${apiBase}/refunds/eligibility/batch`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ order_ids: orderIds, include_subscription: includeSubscription }),
    });
    return handleResponse(response);
  },
  async requestRefund(kind: 'pack' | 'subscription', orderId?: string): Promise<RefundResponse> {
    const response = await fetchWithRetry(`${apiBase}/refunds/request`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ kind, order_id: orderId }),
    });
    return handleResponse(response);
  },
};

export const apiBaseSchema = z
  .string()
  .min(1)
  .refine(
    (value) => value.startsWith('/') || z.string().url().safeParse(value).success,
    'VITE_API_BASE must be an absolute URL or root-relative path'
  )
  .default(DEFAULT_API_BASE);

/** Check if an error is an insufficient credits error from the API */
export function isInsufficientCreditsError(err: unknown): boolean {
  const msg = (err as Error)?.message ?? '';
  return msg.includes('insufficient-credits') || msg.includes('Insufficient Credits');
}
